import { replaceHistory } from "@tm/insurance/utils";
import { COLLEAGUES_TITLE } from "@tm/isomorphic-utils";
import { useBreakpointV2 } from "@tm/ui-widgets";
import React, { Dispatch, SetStateAction, useEffect } from "react";
import FlexComp from "../FlexComp/FlexComp";
import FormattedText from "../FormattedText/FormattedText";
import {
  Badge,
  ColleagueTitle,
  Dot,
  Dropdown,
  EmailSelectorBackdrop,
  InputIcon,
  InputWrapper,
  Name,
  NoUserFoundMessage,
  OptionContent,
  OptionEmail,
  OptionRow as StyledOptionRow,
  SelectorDescription,
  SelectorWrapper,
  TravellerInput,
} from "./TravellerEmailSelector.style";

export type TravellerOption = {
  id: string;
  text: string;
  subText: string;
  badge?: string;
  color: string;
};

export interface TravellerEmailSelectorProps {
  label: string;
  description: string;
  options: TravellerOption[];
  colleagueOptions?: TravellerOption[];
  onClose: () => void;
  onSelect: Dispatch<
    SetStateAction<{ text: string; subText: string; id: string }>
  >;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const TravellerEmailSelector: React.FC<TravellerEmailSelectorProps> = ({
  label,
  description,
  options,
  colleagueOptions,
  onClose,
  onSelect,
  onSearch,
}) => {
  const device = useBreakpointV2();

  useEffect(() => {
    return () => {
      onClose();
    };
  }, []);

  const handleSelect = (option: TravellerOption) => {
    onSelect({
      text: option?.text || "",
      subText: option?.subText || "",
      id: option?.id || "",
    });
    replaceHistory("bookingFor", option?.id || "");
    onClose();
  };

  const isNoUserFound =
    (!options || options?.length === 0) &&
    (!colleagueOptions || colleagueOptions?.length === 0);

  const renderOptions = (options: TravellerOption[]) => {
    return options?.map((option) => (
      <StyledOptionRow key={option.id} onClick={() => handleSelect(option)}>
        <Dot color={option.color} />
        <OptionContent>
          <FlexComp>
            <Name>{option.text}</Name>
            <Badge color={option.color}>{option.badge}</Badge>
          </FlexComp>
          <OptionEmail>{option.subText}</OptionEmail>
        </OptionContent>
      </StyledOptionRow>
    ));
  };

  const selectorComp = (
    <SelectorWrapper onClick={(e) => e.stopPropagation()}>
      <InputWrapper>
        <InputIcon>
          <span role="img" aria-label="user">
            👤
          </span>
        </InputIcon>
        <TravellerInput
          onChange={(e) => {
            onSearch(e);
          }}
          placeholder={label}
          autoFocus
        />
      </InputWrapper>
      <SelectorDescription>
        <FormattedText value={description} />
      </SelectorDescription>
      <Dropdown>
        {isNoUserFound ? (
          <NoUserFoundMessage>No user found with this name</NoUserFoundMessage>
        ) : (
          <>
            {renderOptions(options)}
            {colleagueOptions && colleagueOptions?.length > 0 && (
              <>
                <ColleagueTitle>{COLLEAGUES_TITLE}</ColleagueTitle>
                {renderOptions(colleagueOptions)}
              </>
            )}
          </>
        )}
      </Dropdown>
    </SelectorWrapper>
  );

  return device === "sm" ? (
    <EmailSelectorBackdrop onClick={onClose}>
      {selectorComp}
    </EmailSelectorBackdrop>
  ) : (
    selectorComp
  );
};

export default TravellerEmailSelector;
